import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { validateMaxCurrentInput } from './maxCurrentValidation';

export interface ConfigureMaxCurrentModalProps {
    open: boolean;
    initialValue: number | null;
    saving?: boolean;
    onClose: () => void;
    onSave: (value: number) => void | Promise<void>;
}

const ConfigureMaxCurrentModal: FunctionComponent<ConfigureMaxCurrentModalProps> = ({
    open,
    initialValue,
    saving = false,
    onClose,
    onSave,
}) => {
    const [rawValue, setRawValue] = useState('');
    const [fieldError, setFieldError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setFieldError(null);
        if (initialValue != null && initialValue > 0) {
            setRawValue(String(initialValue).replace('.', ','));
        } else {
            setRawValue('');
        }
    }, [open, initialValue]);

    const handleSave = async () => {
        const result = validateMaxCurrentInput(rawValue);
        if (!result.ok) {
            setFieldError(result.message);
            return;
        }
        setFieldError(null);
        try {
            await onSave(result.value);
        } catch {
            // Keep the dialog open so the user can retry after the caller reports the error.
        }
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            aria-labelledby="configure-max-current-title"
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="configure-max-current-title">Configurar Corrente Máxima</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Corrente máxima (A)"
                    fullWidth
                    value={rawValue}
                    onChange={(e) => {
                        setRawValue(e.target.value);
                        if (fieldError) {
                            setFieldError(null);
                        }
                    }}
                    error={Boolean(fieldError)}
                    helperText={fieldError || 'Valor numérico com até uma casa decimal'}
                    inputProps={{
                        inputMode: 'decimal',
                    }}
                    disabled={saving}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    aria-label="configure-max-current-save"
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfigureMaxCurrentModal;
